import templateUrl from './resourceListTh.html'
import './resourceListTh.scss'

/**
 * Component with resource list header with optional click-sort and select all/none checkbox.
 * Used inside the component tmrResourceList.
 *
 * @example <caption>Basic order by</caption>
 *   <tmr-resource-list-th
 *     label="RESOURCELIST.CREATIONDATE"
 *     order-by="CREATION_DATE"
 *   ></tmr-resource-list-th>
 *
 * @example <caption>Advanced order by (for routes with multiple orderBys/pagers)</caption>
 *   <tmr-resource-list-th
 *     label="RESOURCELIST.CREATIONDATE"
 *     order-by="CREATION_DATE"
 *     order-by-id="someUniqueOrderById"
 *   ></tmr-resource-list-th>
 *
 * @example <caption>Sorting of custom lists (not loaded from backend)</caption>
 * // Route:
 * {
 *   ...,
 *   params: {
 *     orderBy: { value: 'some_property', type: 'string', squash: true, dynamic: true },
 *     asc: { value: false, type: 'bool', squash: true, dynamic: true }
 *   }
 * }
 *
 * // Controller
 * {
 *   ...
 *
 *   constructor($stateParams) { 'ngInject';
 *     Object.assign(this, { $stateParams });
 *   }
 *
 *   orderBy() {
 *     return (this.$stateParams.asc ? '' : '-') + this.$stateParams.orderBy;
 *   }
 * }
 *
 * // Template
 * <tmr-resource-list-th order-by="some_property"></tmr-resource-list-th>
 * ...
 * <tmr-resource ng-repeat="row in $ctrl.list | orderBy:$ctrl.orderBy()">
 *
 * @type {ngComponent}
 * @prop {string} label The translation key for the heading label.
 * @prop {string=} order-by The sort property added to the query
 * @prop {string=} order-by-id An optional per-route identifier for the orderBy. Allows for multiple orderBys/pagers on the same route
 * @prop {boolean=} check-all
 * @prop {boolean=} is-checked
 * @prop {boolean=} is-indeterminate
 * @prop {function=} toggle-all
 */
export const tmrResourceListTh = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrResourceListThController',
  bindings: {
    label: '@',
    orderBy: '@',
    orderById: '@?',
    checkAll: '<?',
    isChecked: '<?',
    isIndeterminate: '<?',
    toggleAll: '&?',
  },
}

/**
 * Controller of component {@link tmrResourceListTh}.
 *
 * @type {ngController}
 */
export class tmrResourceListThController {
  static ngType = 'controller'

  constructor($state, $stateParams, $translate) {
    'ngInject'
    Object.assign(this, { $state, $stateParams, $translate })
  }

  $onInit() {
    if (this.orderBy) {
      if (this.checkAll)
        throw new Error(
          "Component tmrResourceListTh doesn't support sorting and checkAll checkbox at the same time"
        )

      this.directionLabels = {
        asc: 'RESOURCELISTTH.DIRECTIONASC',
        desc: 'RESOURCELISTTH.DIRECTIONDESC',
        none: 'RESOURCELISTTH.DIRECTIONNONE',
      }

      // Translate label here because it's needed as variable for the direction label
      this.$translate(this.label).then(
        translated => (this.translatedLabel = translated)
      )
    }
  }

  onSort() {
    return this.$state.go('.', {
      [this.orderByParam]: this.orderBy,
      [this.ascParam]: ['desc', 'none'].includes(this.direction),
    })
  }

  get ascParam() {
    return this.orderById ? `asc.${this.orderById}` : 'asc'
  }

  get orderByParam() {
    return this.orderById ? `orderBy.${this.orderById}` : 'orderBy'
  }

  get direction() {
    let ascDesc = this.$stateParams[this.ascParam] ? 'asc' : 'desc'
    return this.$stateParams[this.orderByParam] == this.orderBy
      ? ascDesc
      : 'none'
  }
}
